"use client"

import { useCallback, useEffect, useRef, useState } from "react"
import { FrameContexts, type app } from "@microsoft/teams-js"
import Cookies from "js-cookie"

import {
  authenticateWithMagicAuth,
  getOAuthUrls,
  getSSOAuthorizationUrl,
  handleEmailSignin,
} from "@/app/(unauthenticated)/auth/signin/actions"
import { openPopupForAuthentication } from "@/app/(unauthenticated)/popup/util"
import { AuthMode } from "@/components/auth/auth-mode"
import OTPVerificationPage from "@/components/auth/otp-verification-page"
import { UnifiedAuthForm } from "@/components/auth/unified-auth-form"
import { routes } from "@/config/routes"
import { usePageTitle } from "@/hooks/use-page-title"
import { trackPageViewed } from "@/lib/analytics-actions"
import {
  getOnboardingReturnPath,
  getPostLoginReturnPath,
} from "@/lib/chat-panel-state"
import {
  buildSlackInstallChainUrl,
  shouldChainSlackInstall,
} from "@/lib/slack-install-chain"
import { isValidRelativePath } from "@/lib/utils"
import { useTeams } from "@/providers/ms-teams-context"
import type { AuthState } from "@/types/auth"

/**
 * Best-effort sanitize of `document.referrer` before we forward it through
 * OAuth state and on to Rails. We only accept http(s):// URLs; everything
 * else (empty string when blocked by Referrer-Policy, in-app navigations,
 * non-string values) collapses to undefined so the resolver falls back to
 * `default` cleanly instead of trying to regex-match garbage.
 */
function sanitizeReferrer(raw: string | null | undefined): string | undefined {
  if (!raw) return undefined
  if (!/^https?:\/\//i.test(raw)) return undefined
  return raw
}

function sanitizeReturnTo(value: unknown): string | undefined {
  return typeof value === "string" && isValidRelativePath(value)
    ? value
    : undefined
}

enum AuthStep {
  SignIn = "signin",
  OtpVerification = "otp_verification",
}

interface UnifiedAuthPageProps {
  searchParams?: Promise<Record<string, string | string[] | undefined>>
  initialAuthMode: AuthMode
}

function teamsEmailHint(
  user: app.Context["user"] | undefined
): string | undefined {
  const candidate = user?.userPrincipalName || user?.loginHint
  return candidate?.includes("@") ? candidate : undefined
}

export function UnifiedAuthPage({
  searchParams: _searchParams,
  initialAuthMode,
}: UnifiedAuthPageProps) {
  const isSignUp = initialAuthMode === AuthMode.SignUp
  usePageTitle(isSignUp ? "Sign Up" : "Sign In")
  const [currentStep, setCurrentStep] = useState<AuthStep>(AuthStep.SignIn)
  const [userEmail, setUserEmail] = useState("")
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [authState, setAuthState] = useState<AuthState>({ error: null })
  const [googleOAuthUrl, setGoogleOAuthUrl] = useState("")
  const [microsoftOAuthUrl, setMicrosoftOAuthUrl] = useState("")
  const [slackOAuthUrl, setSlackOAuthUrl] = useState("")
  const [loginHint, setLoginHint] = useState<string | null>(null)
  const [returnTo, setReturnTo] = useState<string | null>(null)
  const [onboardingScenario, setOnboardingScenario] = useState<string | null>(
    null
  )
  // document.referrer captured at first mount. Forwarded through OAuth state
  // and magic-auth FormData so Rails' onboarding chat scenario resolver can
  // fall back to it when no `returnTo` deep link matches. See sanitizeReferrer
  // above for the http(s):// guard.
  const [referrer, setReferrer] = useState<string>()
  const [organizationId, setOrganizationId] = useState<string | null>(null)
  const [trigger, setTrigger] = useState(false)
  const [pendingAuthenticationToken, setPendingAuthenticationToken] = useState<
    string | null
  >(null)

  // OAuth URLs, the SSO auto-trigger and email/OTP submit all depend on whether
  // we are in Teams (popup vs full-page navigation), so they wait for
  // detection; the fields stay editable meanwhile.
  const { isInTeams, isTeamsResolved, teamsContext } = useTeams()
  const frameContext = teamsContext?.page.frameContext
  // Outlook and the Microsoft 365 hub put Entra's opaque login_hint claim in
  // `loginHint` (Teams puts the UPN there), so only prefill an email-shaped value.
  const teamsLoginHint = teamsEmailHint(teamsContext?.user)

  // Fire page view event for signup/login pages (server-side to bypass ad blockers)
  const pageViewTracked = useRef(false)
  useEffect(() => {
    if (pageViewTracked.current) return
    pageViewTracked.current = true

    const pageName = isSignUp ? "Signup Page Viewed" : "Login Page Viewed"
    const properties: Record<string, unknown> = {}
    if (!isSignUp) {
      const url = new URL(window.location.href)
      const returnUrl = url.searchParams.get("returnTo")
      if (returnUrl) properties.return_url = returnUrl
    }
    // Best-effort: link scanners and tab closes abort this POST mid-flight;
    // a failure is a warning in the logs, not an unhandled rejection.
    trackPageViewed(pageName, properties).catch((error: unknown) => {
      // eslint-disable-next-line no-console
      console.warn("[Auth] trackPageViewed failed:", error)
    })
  }, [isSignUp])

  // Capture document.referrer once on mount. It is sticky for the lifetime
  // of this page render so a soft navigation triggered by toggling
  // sign-in <-> sign-up does not blow it away.
  useEffect(() => {
    setReferrer((prev) => prev ?? sanitizeReferrer(document.referrer))
  }, [])

  // Extract authentication parameters from URL and Teams context
  useEffect(() => {
    const extractSearchParams = async () => {
      if (_searchParams) {
        const params = await _searchParams

        // LOGIN HINT: for email pre-fill in Teams context
        const hint = teamsLoginHint || (params.loginHint as string)
        if (hint) {
          setLoginHint(hint)
          setUserEmail(hint)
        }

        // RETURNTO: Extract destination URL to redirect after successful auth
        setReturnTo(sanitizeReturnTo(params.returnTo) ?? null)

        const onboardingScenarioParam = params.onboarding_scenario
        if (
          onboardingScenarioParam &&
          typeof onboardingScenarioParam === "string"
        ) {
          setOnboardingScenario(onboardingScenarioParam)
        }

        // ERROR: Display error message passed via query param (e.g., from middleware redirect)
        const errorParam = params.error
        if (errorParam && typeof errorParam === "string") {
          setAuthState({ error: errorParam })
        }

        // ORGANIZATION ID: Extract organization ID for SSO
        const orgId = params.orgId || params.organizationId
        if (orgId && typeof orgId === "string") {
          setOrganizationId(orgId)
        }

        // TRIGGER: Extract trigger parameter for automatic authentication
        const triggerParam = params.trigger
        if (triggerParam === "true") {
          setTrigger(true)
        }

        // EMAIL VERIFICATION: Check if this is an email verification flow
        const requiresEmailVerification =
          params.requiresEmailVerification === "true"
        if (requiresEmailVerification) {
          // Set email verification state
          const email = params.email as string
          const token = params.pendingAuthenticationToken as string

          if (email && token) {
            setUserEmail(email)
            setPendingAuthenticationToken(token)
            setCurrentStep(AuthStep.OtpVerification)
          }
        }
      }
    }
    void extractSearchParams()
  }, [_searchParams, teamsLoginHint])

  // Generate OAuth URLs for Google and Microsoft authentication
  useEffect(() => {
    const loadOAuthUrls = async () => {
      if (!isTeamsResolved) return
      const params = await _searchParams
      const currentReturnTo = returnTo || sanitizeReturnTo(params?.returnTo)

      // In Teams context, OAuth needs to return to popup handler
      // Outside Teams, can return directly to the returnTo URL
      const oauthReturnTo = isInTeams
        ? "/popup/in?status=success&result=login" // Teams popup return URL
        : currentReturnTo // Direct return URL

      // One server action for all providers (separate server-action POSTs
      // would run sequentially). `referrer` travels through OAuth state so
      // the post-callback ensureTenantForUser call can forward it to Rails.
      const authOrigin = isSignUp ? routes.auth.signup : routes.auth.signin()
      const urls = await getOAuthUrls(
        oauthReturnTo,
        authOrigin,
        referrer,
        onboardingScenario ?? undefined,
        // Signup "Continue with Slack" carries `slack_install` so the callback
        // chains the bot install; sign-in is pure identity and must not.
        isSignUp ? "slack_install" : (onboardingScenario ?? undefined)
      )

      setGoogleOAuthUrl(urls.google)
      setMicrosoftOAuthUrl(urls.microsoft)
      setSlackOAuthUrl(urls.slack)
    }
    // Same abort pattern as above; the provider buttons stay disabled until a
    // later run (deps settle over the first renders) succeeds.
    loadOAuthUrls().catch((error: unknown) => {
      // eslint-disable-next-line no-console
      console.warn("[Auth] getOAuthUrls failed:", error)
    })
  }, [
    isTeamsResolved,
    isInTeams,
    isSignUp,
    returnTo,
    _searchParams,
    referrer,
    onboardingScenario,
  ])

  // Handle successful authentication. When Rails seeded an onboarding chat
  // for a brand-new user (magic-auth signup path), Rails returns a
  // thread_key which we fold into the returnTo via `appendThreadParam`.
  //
  // We deliberately bypass `getPostLoginReturnPath` in the seeded case so
  // `chatPanelReset=1` is NOT appended — that signal makes
  // ChatPanelResetHandler clear the thread key, which would race with the
  // global ChatThreadDeepLinkHandler that sets it. See the "chatPanelReset
  // race" comment in the design plan.
  const handleAuthSuccess = (
    onboardingThreadKey?: string,
    onboardingPreviewPath?: string
  ) => {
    // Clear the current-tenant-uuid cookie to ensure the middleware
    // picks the correct tenant for the authenticated org
    Cookies.remove("current-tenant-uuid")
    const safeReturnTo =
      returnTo && isValidRelativePath(returnTo) ? returnTo : undefined

    if (onboardingThreadKey) {
      window.location.href = getOnboardingReturnPath(
        safeReturnTo || routes.okrs.list,
        onboardingThreadKey,
        onboardingPreviewPath
      )
      return
    }

    window.location.href = getPostLoginReturnPath({
      returnPath: safeReturnTo,
      // Route to "/" so the server-side (authenticated)/route.ts applies the
      // DB preference (x-landing-page header) before falling back to the cookie.
      // Computing the path client-side from the cookie would silently ignore the
      // DB preference on a new device where the cookie is absent.
      defaultPath: routes.root,
      isPopupContext: false,
    })
  }

  // Handle email sign-in submission
  const handleEmailSubmit = async (formData: FormData) => {
    setIsSubmitting(true)
    const email = formData.get("email") as string
    setUserEmail(email)

    formData.append("authMode", isSignUp ? "signup" : "signin")

    if (returnTo) {
      formData.append("returnTo", returnTo)
    }
    if (referrer) {
      formData.append("referrer", referrer)
    }
    if (onboardingScenario) {
      formData.append("onboarding_scenario", onboardingScenario)
    }

    // Add MS Teams context to formData
    if (isInTeams) {
      formData.append("isPopupContext", "true")
    }

    try {
      const result = await handleEmailSignin(formData)

      if (result.ssoUrl) {
        await handleOAuthSignin(result.ssoUrl, "SSO")
        return { requiresMagicAuth: false }
      } else if (result.requiresMagicAuth) {
        setIsSubmitting(false)
        setAuthState({ error: null })
        setCurrentStep(AuthStep.OtpVerification)
        return { requiresMagicAuth: true }
      } else if (result.error) {
        setIsSubmitting(false)
        setAuthState({ error: result.error })
        return result
      }

      return { requiresMagicAuth: false }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.info("[Auth] handleEmailSubmit error:", error)
      setIsSubmitting(false)
      setAuthState({ error: "Something went wrong. Please try again." })
      return {
        requiresMagicAuth: false,
        error: "Something went wrong. Please try again.",
      }
    }
  }

  /**
   * Handle authentication with external providers.
   * POPUP IS REQUIRED because Google/Microsoft/OKTA are external domains:
   * Flow:
   * 1. In Teams: Uses Teams SDK popup (authentication.authenticate)
   * 2. Outside Teams: Uses standard window.open popup
   * 3. After OAuth/SSO completion: User is redirected back to our domain
   * 4. Popup closes and notifies parent window of success/failure
   */
  const handleOAuthSignin = useCallback(
    async (oauthUrl: string, provider: string) => {
      if (!oauthUrl) {
        // OAuth URL not available - this is expected during initialization
        return
      }

      try {
        if (isInTeams && frameContext !== FrameContexts.authentication) {
          // MS TEAMS OAUTH/SSO FLOW
          // Use Teams SDK popup for external OAuth/SSO providers
          await openPopupForAuthentication({
            url: oauthUrl,
            isInTeams,
            frameContext,
          })
          // After successful OAuth, redirect to main app
          window.location.href = routes.root
        } else {
          // BROWSER OAUTH FLOW
          // Direct navigation to OAuth provider (will redirect back to us)
          window.location.href = oauthUrl
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.info("[Auth] OAuth popup error for provider:", provider, error)
        setAuthState({
          error: `${provider} sign-in failed. Please try again.`,
        })
      }
    },
    [isInTeams, frameContext]
  )

  // Handle automatic trigger flow when trigger=true
  useEffect(() => {
    const handleAutoTrigger = async () => {
      if (!trigger || !isTeamsResolved) return

      // If organizationId is provided, generate authorization url
      if (organizationId) {
        const ssoReturnTo = isInTeams
          ? "/popup/in?status=success&result=login"
          : returnTo || undefined

        const ssoUrl = await getSSOAuthorizationUrl(
          organizationId,
          ssoReturnTo,
          referrer,
          onboardingScenario ?? undefined
        )

        // Reuse handleOAuthSignin for SSO URL
        void handleOAuthSignin(ssoUrl, "SSO")
        return
      }
    }

    void handleAutoTrigger()
  }, [
    trigger,
    isTeamsResolved,
    organizationId,
    handleOAuthSignin,
    isInTeams,
    returnTo,
    referrer,
    onboardingScenario,
  ])

  const handleOTPSubmit = async (prevState: AuthState, formData: FormData) => {
    setIsSubmitting(true)
    if (returnTo) {
      formData.append("returnTo", returnTo)
    }
    if (referrer) {
      formData.append("referrer", referrer)
    }
    if (onboardingScenario) {
      formData.append("onboarding_scenario", onboardingScenario)
    }
    const result = await authenticateWithMagicAuth(prevState, formData)

    // If authentication was successful, handle success
    if (result.success) {
      // Slack signup that tripped WorkOS email verification: the OAuth
      // callback's install chain never ran (onError fires before onSuccess),
      // so resume it here now that the email is verified.
      if (
        shouldChainSlackInstall({
          onboardingScenario,
          isNewTenant: result.isNewTenant,
          canFullPageNavigate: !isInTeams,
        })
      ) {
        const safeReturnTo =
          returnTo && isValidRelativePath(returnTo) ? returnTo : undefined
        window.location.href = buildSlackInstallChainUrl({
          returnPath: safeReturnTo,
          onboardingThreadKey: result.onboardingThreadKey,
        })
        return result
      }

      // result.onboardingThreadKey is set by authenticateWithMagicAuth when
      // Rails seeded an onboarding chat for this signup.
      handleAuthSuccess(
        result.onboardingThreadKey,
        result.onboardingPreviewPath
      )
    } else if (result.redirectToSSO) {
      await handleOAuthSignin(result.redirectToSSO, "SSO")
    } else if (result.error) {
      setIsSubmitting(false)
      setAuthState({ error: result.error })
    }
    return result
  }

  // Handle back to email
  const handleBackToEmail = () => {
    setCurrentStep(AuthStep.SignIn)
    setUserEmail("")
    setIsSubmitting(false)
    setAuthState({ error: null })
    setPendingAuthenticationToken(null)
  }

  // OTP Verification Step
  if (currentStep === AuthStep.OtpVerification) {
    return (
      <OTPVerificationPage
        email={userEmail}
        onCodeSubmit={handleOTPSubmit}
        onBackToEmail={handleBackToEmail}
        isSubmitting={isSubmitting}
        isSubmitDisabled={!isTeamsResolved}
        error={authState.error}
        pendingAuthenticationToken={pendingAuthenticationToken ?? undefined}
      />
    )
  }

  // Auth Form Step
  return (
    <UnifiedAuthForm
      googleOAuthUrl={googleOAuthUrl}
      microsoftOAuthUrl={microsoftOAuthUrl}
      onEmailSubmit={handleEmailSubmit}
      onOAuthSubmit={handleOAuthSignin}
      isSubmitting={isSubmitting}
      isSubmitDisabled={!isTeamsResolved}
      error={authState.error ?? null}
      loginHint={loginHint}
      initialAuthMode={initialAuthMode}
      slackOAuthUrl={slackOAuthUrl}
    />
  )
}
